import React, { useState, useEffect } from 'react'
import { Button } from '../components/ui/Button'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/layout/Navbar'
import { AccountLayout } from '../components/layout/AccountLayout'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { authService } from '../services/authService'
import apiClient from '../services/api'
import ContinueLaterModal from '../components/onboarding/ContinueLaterModal'

export const ApplicationStartPage = () => {
  const navigate = useNavigate()
  const { progress, refreshProgress } = useOnboardingContext()
  const [flowType, setFlowType] = useState<
    'persona_moral' | 'persona_fisica' | null
  >(null)
  const [isLoadingProgress, setIsLoadingProgress] = useState(true)
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false)

  // Debug: Log context state
  console.log(
    '🐛 ApplicationStartPage render - progress flow:',
    progress?.flow?.variant,
    'flowType:',
    flowType,
    'isLoadingProgress:',
    isLoadingProgress
  )

  // Initialize flow type detection only once
  useEffect(() => {
    let isMounted = true

    const getFlowType = async () => {
      console.log('🔍 ApplicationStartPage - Initializing flow type detection')

      // Check URL parameter first (most reliable for our case)
      const urlParams = new URLSearchParams(window.location.search)
      const flowFromUrl = urlParams.get('flow')

      if (flowFromUrl === 'persona_fisica' || flowFromUrl === 'persona_moral') {
        console.log('✅ Setting flowType from URL (PRIORITY):', flowFromUrl)
        if (isMounted) {
          setFlowType(flowFromUrl as 'persona_moral' | 'persona_fisica')
          setIsLoadingProgress(false)
        }
        // No hacer más checks si tenemos URL params válidos
        return
      }

      // Then check if we have progress (without refreshing to avoid loop)
      if (progress?.flow?.variant) {
        const variant = progress.flow.variant as
          | 'persona_moral'
          | 'persona_fisica'
        console.log('✅ Setting flowType from existing progress:', variant)
        if (isMounted) {
          setFlowType(variant)
          setIsLoadingProgress(false)
        }
        return
      }

      // Finally, try to refresh progress only once
      console.log('🔄 Attempting single refresh...')
      try {
        await refreshProgress()
        // Don't set loading to false here, let the progress update handle it
      } catch (error) {
        console.log(
          '⚠️ Could not refresh progress, redirecting to flow selection',
          error
        )
        if (isMounted) {
          // En lugar de defaultear, redirigir a selección de flujo
          navigate('/onboarding/flow-selection')
        }
      }
    }

    // Timeout de seguridad: si después de 5 segundos aún está en loading, redirigir
    // PERO solo si no hay parámetros URL (navegación manual)
    const safetyTimeout = setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search)
      const flowFromUrl = urlParams.get('flow')

      if (isMounted && isLoadingProgress && !flowFromUrl) {
        console.log('🚨 Safety timeout reached, redirecting to flow selection')
        navigate('/onboarding/flow-selection')
      } else if (flowFromUrl) {
        console.log('✅ Safety timeout skipped - URL params present')
      }
    }, 5000)

    getFlowType()

    return () => {
      isMounted = false
      clearTimeout(safetyTimeout)
    }
  }, []) // Remove progress and refreshProgress from dependencies

  // Separate effect to handle progress updates (ONLY if no flowType from URL)
  useEffect(() => {
    // Si ya tenemos flowType desde URL, no hacer nada más
    if (flowType) {
      console.log('🎯 FlowType already set, skipping progress-based detection')
      return
    }

    if (progress?.flow?.variant && !flowType) {
      const variant = progress.flow.variant as
        | 'persona_moral'
        | 'persona_fisica'
      console.log('✅ Setting flowType from progress update:', variant)
      setFlowType(variant)
      setIsLoadingProgress(false)
    } else if (progress && !progress?.flow?.variant && !flowType) {
      // Solo redirigir si NO hay parámetros URL
      const urlParams = new URLSearchParams(window.location.search)
      const flowFromUrl = urlParams.get('flow')

      if (flowFromUrl === 'persona_fisica' || flowFromUrl === 'persona_moral') {
        console.log(
          '🎯 URL params present, not redirecting - waiting for first effect'
        )
        return
      }

      console.log(
        '⚠️ No flow variant and no URL params, redirecting to flow selection'
      )
      navigate('/onboarding/flow-selection')
      return
    }
  }, [progress, flowType, navigate])

  // Debug log when flowType changes
  useEffect(() => {
    console.log('🎯 ApplicationStartPage - FlowType changed to:', flowType)
  }, [flowType])

  const handleContinue = async () => {
    console.log(
      '🚀 ApplicationStartPage - Continue clicked, flowType:',
      flowType
    )
    console.log('🚀 ApplicationStartPage - Progress at continue:', progress)

    // Mostrar estado de loading
    setIsUpdatingProgress(true)

    // Determinar la siguiente URL según el tipo de flujo
    let nextUrl: string
    if (flowType === 'persona_fisica') {
      nextUrl = '/onboarding/personal-data'
      console.log('✅ Next URL for Persona Física:', nextUrl)
    } else {
      nextUrl = '/onboarding/company-data'
      console.log('✅ Next URL for Persona Moral:', nextUrl)
    }

    try {
      // Obtener el credit application ID
      const prospectoQuoteId = localStorage.getItem('prospectoQuoteId')
      const token = localStorage.getItem('auth_token')
      if (prospectoQuoteId && token) {
        await apiClient.patch(
          `/cotizador/mis-cotizaciones-prospecto/${prospectoQuoteId}/estatus`,
          { estatus: 'Datos Básicos' },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        console.log('✅ Estatus actualizado exitosamente')
      } else {
        console.warn(
          '⚠️ prospectoQuoteId o token no encontrado en localStorage'
        )
      }

      const creditAppId = await authService.getCurrentCreditApplicationId()
      if (!creditAppId) {
        console.warn(
          '⚠️ No credit application ID found, navigating without updating progressURL'
        )
        setIsUpdatingProgress(false)
        navigate(nextUrl)
        return
      }

      // Actualizar progressURL antes de navegar
      console.log(
        '📍 Updating progressURL to:',
        nextUrl,
        'for credit app:',
        creditAppId
      )
      await authService.updateCreditApplicationProgress(creditAppId, nextUrl)
      console.log('✅ ProgressURL updated successfully!')

      // Breve delay para mostrar el feedback visual
      await new Promise(resolve => setTimeout(resolve, 500))

      // Navegar al siguiente paso
      console.log('🧭 Navigating to:', nextUrl)
      navigate(nextUrl)
    } catch (error) {
      console.error('❌ Error updating progressURL:', error)
      setIsUpdatingProgress(false)
      // Si falla la actualización, navegar de todas formas
      navigate(nextUrl)
    }
  }

  const getRequirements = () => {
    if (flowType === 'persona_fisica') {
      return [
        {
          emoji: '📄',
          title: 'Datos personales y fiscales',
          description: 'Nombre, RFC, dirección, contacto.',
        },
        {
          emoji: '🪪',
          title: 'Identificación oficial',
          description: 'Credencial INE por ambos lados, vigente y legible.',
        },
        {
          emoji: '🔐',
          title: 'Clave del SAT (CIEC)',
          description: 'Para realizar la consulta fiscal de forma segura.',
        },
        {
          emoji: '🏢',
          title: 'Documentos legales',
          description: 'Constancia de situación fiscal u otros documentos.',
        },
        {
          emoji: '🏦',
          title: 'Estados de cuenta bancarios',
          description: 'De los últimos seis meses, en formato PDF.',
        },
        {
          emoji: '🏠',
          title: 'Comprobante de domicilio',
          description:
            'No mayor a 3 meses, a nombre del solicitante o empresa.',
        },
      ]
    } else {
      return [
        {
          emoji: '📄',
          title: 'Datos personales y fiscales',
          description:
            'Representante legal y Socio mayoritario, RFC, contacto.',
        },
        {
          emoji: '🪪',
          title: 'Identificación oficial',
          description: 'Credencial INE por ambos lados, vigente y legible.',
        },
        {
          emoji: '🔐',
          title: 'Clave del SAT (CIEC)',
          description: 'Para realizar la consulta fiscal de forma segura.',
        },
        {
          emoji: '🏢',
          title: 'Documentos legales',
          description:
            'Acta constitutiva u otros documentos que acrediten la empresa.',
        },
        {
          emoji: '🏦',
          title: 'Estados de cuenta bancarios',
          description: 'De los últimos seis meses, en formato PDF.',
        },
        {
          emoji: '🏠',
          title: 'Comprobante de domicilio',
          description:
            ' No mayor a 3 meses, a nombre del solicitante o empresa.',
        },
      ]
    }
  }

  const requirements = getRequirements()

  // Show loading while determining flow type
  if (isLoadingProgress) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Determinando tipo de flujo...</p>
          <p className="text-xs text-gray-400 mt-2">
            FlowType: {flowType || 'detectando...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="min-h-screen flex flex-col md:hidden">
        {/* Mobile Header */}
        <Navbar logoSize="large" />

        {/* Mobile Content */}
        <div className="flex-1 bg-olr-white p-4 sm:p-6">
          <div className="max-w-md mx-auto">
            {/* Progress update indicator */}
            {isUpdatingProgress && (
              <div className="bg-orange-100 border border-orange-200 rounded p-3 mb-4 text-sm text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                  <span className="text-orange-800">
                    <strong>Actualizando progreso...</strong> Configurando
                    siguiente paso
                  </span>
                </div>
              </div>
            )}

            <h1 className="text-lg sm:text-h3 text-olr-dark mb-4 sm:mb-6 text-center font-semibold">
              {flowType === 'persona_fisica'
                ? '¡Comencemos tu solicitud personal!'
                : 'Estamos listos para empezar'}
            </h1>

            <p className="text-sm sm:text-paragraph-2 text-olr-body mb-6 sm:mb-8 text-center leading-relaxed">
              Vamos a guiarte paso a paso para completar tu solicitud de
              arrendamiento.
              <br />
              Antes de comenzar, ten disponible la siguiente información y
              documentación:
            </p>

            <div className="space-y-4 sm:space-y-5 mb-6 sm:mb-8">
              {requirements.map((req, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                >
                  <span className="text-lg sm:text-xl flex-shrink-0 mt-0.5">
                    {req.emoji}
                  </span>
                  <div>
                    <h3 className="text-sm sm:text-paragraph-1 font-semibold text-olr-dark mb-1">
                      {req.title}
                    </h3>
                    <p className="text-xs sm:text-paragraph-2 text-olr-body leading-relaxed">
                      {req.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-paragraph-2 text-olr-body mb-8 text-center">
              Contar con esta información desde el inicio facilitará el registro
              y permitirá una evaluación más ágil de tu solicitud.
            </p>

            <div className="space-y-4">
              <Button
                variant="primary"
                className="w-full"
                onClick={handleContinue}
                disabled={isUpdatingProgress}
              >
                {isUpdatingProgress ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Actualizando progreso...</span>
                  </div>
                ) : (
                  'Continuar'
                )}
              </Button>
              <ContinueLaterModal />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout: mismo shell de cuenta (Navbar + sidebar flotante)
          que "Mis cotizaciones" y las demás páginas de onboarding. */}
      <div className="hidden md:block">
        <AccountLayout showSidebar={false}>
          <div className="bg-olr-white rounded-lg shadow-card w-full px-16 py-8">
            {/* Progress update indicator - Desktop */}
            {isUpdatingProgress && (
              <div className="bg-orange-100 border border-orange-200 rounded p-4 mb-8 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                  <span className="text-orange-800 text-paragraph-1">
                    <strong>Actualizando progreso...</strong> Configurando
                    siguiente paso
                  </span>
                </div>
              </div>
            )}

            <div className="mb-10">
              <h1 className="text-h3 text-olr-dark mb-6 text-center">
                Estamos listos para empezar
              </h1>

              <p className="text-paragraph text-olr-body max-w-2xl mx-auto">
                Vamos a guiarte paso a paso para completar tu solicitud de
                arrendamiento.
                <br />
                Antes de comenzar, ten disponible la siguiente información y
                documentación:
              </p>
            </div>

            <div className="max-w-2xl mx-auto mb-8">
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <span className="text-2xl flex-shrink-0 mt-1">•</span>
                    <div>
                      <h3 className="text-paragraph font-semibold text-olr-dark mb-2">
                        {req.emoji} {req.title}
                      </h3>
                      <p className="text-paragraph text-olr-body">
                        {req.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-10">
              <p className="text-paragraph text-olr-body max-w-2xl mx-auto">
                Contar con esta información desde el inicio facilitará el
                registro y permitirá una evaluación más ágil de tu solicitud.
              </p>
            </div>

            <div className="flex justify-center gap-4">
              {/* <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isUpdatingProgress}
              >
                Cancelarr
              </Button> */}
              <ContinueLaterModal />

              <Button
                className="px-12 py-3 min-w-[230px]"
                variant="primary"
                onClick={handleContinue}
                disabled={isUpdatingProgress}
              >
                {isUpdatingProgress ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Cargando...</span>
                  </div>
                ) : (
                  'Continuar'
                )}
              </Button>
            </div>
          </div>
        </AccountLayout>
      </div>
    </>
  )
}
